import { Box, Stack, Typography } from '@mui/material';
import type { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { netsuiteSyncWizardSelectors } from '@vy/app-selectors';
import { isNotNullOrUndefined, validateNotNullOrUndefined } from '@vy/utils';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { VyTable } from '../../../../../../../components/new-design/table/vy-table/VyTable';
import { useContractProductsOverviews } from '../../../../../../../hooks/requests/use-contract-products-overviews';
import { hasNetSuiteIntegration } from '../../../../../../../utils/integration-utils';
import { createContractProductsOverviewsColumns } from '../constants/contract-products-overviews-columns';
import { STEP_TITLE_SX, TABLE_CONTAINER_SX, TABLE_SX } from '../constants/net-suite-sync-wizard-styles';

type ProductSelectionStepProps = {
  contractId: string;
  selectedProductsIds: string[];
  setSelectedProductsIds: (ids: string[]) => void;
  onAddErpId: (productId: string) => void;
};

export const ProductSelectionStep: FC<ProductSelectionStepProps> = ({
  contractId,
  selectedProductsIds,
  setSelectedProductsIds,
  onAddErpId,
}) => {
  const { data, isFetching } = useContractProductsOverviews(contractId);

  const { productsOverviews } = data ?? {};
  validateNotNullOrUndefined(productsOverviews, 'productsOverviews');

  const columns = useMemo(() => createContractProductsOverviewsColumns({ onAddErpId }), [onAddErpId]);

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      const currentProductsIds = Array.from(model.ids).map((key) => key.toString());
      setSelectedProductsIds(currentProductsIds);
    },
    [setSelectedProductsIds],
  );

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({ type: 'include', ids: new Set(isFetching ? [] : (selectedProductsIds ?? [])) }),
    [isFetching, selectedProductsIds],
  );

  if (!isNotNullOrUndefined(productsOverviews)) {
    return null;
  }

  return (
    <Stack data-vy={netsuiteSyncWizardSelectors.productStep} sx={{
      gap: 2
    }}>
      <Typography sx={STEP_TITLE_SX}>First, select product to sync</Typography>
      <Box sx={TABLE_CONTAINER_SX}>
        <VyTable
          tableName="product-selection"
          columns={columns}
          checkboxSelection={true}
          disableRowSelectionOnClick
          onRowSelectionModelChange={handleSelectionChange}
          items={productsOverviews}
          itemsCount={productsOverviews.length}
          rowSelectionModel={rowSelectionModel}
          getRowId={(row) => row.id}
          showPagination={false}
          isRowSelectable={(params: GridRowParams) => hasNetSuiteIntegration(params.row.integrationEntities)}
          slotProps={{
            baseCheckbox: {
              onClick: (event) => event.stopPropagation(),
            },
          }}
          sx={TABLE_SX}
          columnHeaderHeight={50}
          rowHeight={58}
        />
      </Box>
    </Stack>
  );
};
